/**
 * Inbound MIDI event store — real production home for `midi.in`, and as of
 * 2026-08-27 it is **LIVE**. Replaces the legacy bridge's
 * `bridge.on('midi.in', ...)` event bus (packages/bridge/, deleted
 * 2026-08-20) as `StatusLed.tsx`'s sole data source.
 *
 * `midi/inboundFeed.ts` calls `recordMidiIn()` per event, exactly as this
 * comment used to predict would be needed. It subscribes to `onMidiIn()`
 * (`opbridge/consumerWs.ts`), so this store is fed identically whether the
 * transitional `/midi/recent` poll or OPBridge's eventual `/ws/consumer`
 * `midi_in` push produced the event — retiring the poll does not touch this
 * file or `StatusLed.tsx`.
 *
 * Verified end to end 2026-08-27: a real MIDI message sent through OPBridge
 * to a real loopback port arrived at a real consumer in the running PWA.
 *
 * Shape: a single "last event" signal, not a per-key map — StatusLed reads
 * it and checks the event against its OWN sourceType/channel/number/threshold,
 * same one-shot filter-per-message idiom the old `bridge.on(...)` callback
 * used. A per-key map would be premature structure with zero evidence for
 * what shape real traffic needs (per-bridge tagging is explicitly still
 * being asked for).
 */

import { createSignal, type Accessor } from 'solid-js';

export interface MidiInEvent {
  kind: 'noteon' | 'noteoff' | 'cc';
  /** Note number (noteon/noteoff) or controller number (cc). */
  number: number;
  /** Velocity (noteon/noteoff) or value (cc). */
  value: number;
  channel: number;
  /** Wall-clock ms this event was recorded. */
  ts: number;
}

const [lastEvent, setLastEvent] = createSignal<MidiInEvent | null>(null);

export const midiInLast: Accessor<MidiInEvent | null> = lastEvent;

/** Record one inbound MIDI event. Called per event by
 *  `midi/inboundFeed.ts` — see module doc. */
export function recordMidiIn(msg: Omit<MidiInEvent, 'ts'>): void {
  setLastEvent({ ...msg, ts: Date.now() });
}
